import { useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { useMember } from "../context/memberContext";
import { useMediaQuery } from "../hooks/useMediaQuery";
import useToggle from "../hooks/useToggle";
import { useTranslations } from "../hooks/useTranslations";
import SVGDescription from "../icons/Description.svg?react";
import SVGDomain from "../icons/Domain.svg?react";
import SVGEqualizer from "../icons/Equalizer.svg?react";
import SVGHistoryEdu from "../icons/HistoryEdu.svg?react";
import SVGHome from "../icons/Home.svg?react";
import SVGSupervisorAccount from "../icons/SupervisorAccount.svg?react";
import { departmentSlugs, Permission } from "../types";
import { flattenPermissions, includesAny } from "../utils";
import styles from "./CustomerApp.module.css";
import Header from "./Header";
import Sidebar from "./Sidebar";
import Accounting from "./accounting/Accounting";
import AgreementDetail from "./agreements/AgreementDetail";
import Agreements from "./agreements/Agreements";
import BilanzOnline from "./bilanzOnline/BilanzOnline";
import Documents from "./documents/Documents";
import Home from "./home/Home";
import {
  SVGBulletAccounting,
  SVGBulletJoinHospitality,
  SVGBulletPayroll,
  SVGBulletWebMarketing,
} from "./icons/Bullet";
import Invites from "./invites/Invites";
import JoinHospitality from "./joinHospitality/JoinHospitality";
import JoinJobOffers from "./joinHospitality/JoinJobOffers";
import MasterData from "./masterData/MasterData";
import {
  accountingDocumentPermissions,
  payrollDocumentPermissions,
} from "./member/DocumentPermissions";
import MemberSelect from "./member/MemberSelect";
import MemberSettings from "./member/MemberSettings";
import MemberUser from "./member/MemberUser";
import OpenItems from "./openItems/OpenItems";
import ArbeitAufAbruf from "./payroll/ArbeitAufAbruf";
import Arbeitssicherheit from "./payroll/Arbeitssicherheit";
import EmergencyService from "./payroll/EmergencyService";
import Payroll from "./payroll/Payroll";
import UniUrg from "./payroll/UniUrg";
import ErrorPage from "./ui/ErrorPage";
import ModalDialog from "./ui/ModalDialog";
import Nav from "./ui/Nav";
import Dac7 from "./webMarketing/Dac7";
import WebMarketing from "./webMarketing/WebMarketing";

const POPUP_KEY = "join-hospitality-cooldown";
const NEVER_SHOW = "never";
const MONTH_MS = 31 * 24 * 60 * 60 * 1000;

const shouldShowJoinPopup = (): boolean => {
  const stored = localStorage.getItem(POPUP_KEY);

  // Never shown before
  if (stored === null) return true;

  // Permanently dismissed
  if (stored === NEVER_SHOW) return false;

  // Check if cooldown period has expired
  const dismissedUntil = parseInt(stored, 10);
  if (isNaN(dismissedUntil)) {
    localStorage.removeItem(POPUP_KEY);
    return true;
  }

  const now = Date.now();
  if (now >= dismissedUntil) {
    localStorage.removeItem(POPUP_KEY);
    return true;
  }

  return false;
};

const setJoinPopupCooldown = (months: number | "never") => {
  if (months === "never") {
    localStorage.setItem(POPUP_KEY, NEVER_SHOW);
  } else {
    const dismissUntil = Date.now() + months * MONTH_MS;
    localStorage.setItem(POPUP_KEY, dismissUntil.toString());
  }
};

const CustomerApp = () => {
  const { i18n } = useTranslations();
  const auth = useAuth();
  const matchesMedia = useMediaQuery("(min-width: 1400px)");
  const sidebar = useToggle();
  const { member, status, error } = useMember();

  const [latchedError, setLatchedError] = useState<unknown>(undefined);
  useEffect(() => {
    // Preserve and render thrown error. Remove only when and
    // if (another) member has been loaded successfully.
    if (error) {
      setLatchedError(error);
    } else if (member) {
      setLatchedError(undefined);
    }
  }, [error, member]);

  const userMemberPermissions = member ? flattenPermissions(member) : [];

  const hasMasterData = member !== undefined;
  const hasMemberUsers = includesAny(userMemberPermissions, [
    Permission.MemberUsersRead,
    Permission.MemberUsersWrite,
  ]);
  const hasMemberUsersWrite = userMemberPermissions.includes(
    Permission.MemberUsersWrite,
  );
  const hasDocuments = includesAny(userMemberPermissions, [
    ...accountingDocumentPermissions,
    ...payrollDocumentPermissions,
  ]);
  const hasBilanzOnline = includesAny(userMemberPermissions, [
    Permission.AccountingsRead,
    Permission.WageCostsRead,
  ]);
  const hasAgreements = userMemberPermissions.includes(
    Permission.AgreementsExecute,
  );
  const hasOpenItems = userMemberPermissions.includes(Permission.OpenItemsRead);
  const hasUniUrg = userMemberPermissions.includes(
    Permission.EmployeeRegistrationExecute,
  );
  const hasArbeitssicherheit = includesAny(userMemberPermissions, [
    Permission.DocumentsBetriebOnlineServiceRead,
    Permission.DocumentsBetriebStandardRead,
    Permission.DocumentsArbeitsvertraegeRead,
  ]);
  const isPayrollCustomer = member?.giottoId !== undefined;
  const hasDac7 = userMemberPermissions.includes(Permission.Dac7Write);

  const closeMenu = () => {
    !matchesMedia && sidebar.close();
  };

  useEffect(closeMenu, [matchesMedia]);
  const [isModalOpen, setIsModalOpen] = useState(shouldShowJoinPopup);

  if (!auth.session) {
    return null;
  }

  return (
    <>
      <Header
        hamburger={{
          isOpen: sidebar.isOpen,
          onToggle: sidebar.toggle,
        }}
      />
      <div>
        <Sidebar isOpen={sidebar.isOpen} close={closeMenu}>
          <div className={styles.memberSelect}>
            <MemberSelect theme="dark" />
            <hr className={styles.separator} />
          </div>
          {status === "success" && (
            <Nav>
              <Nav.Item
                text={i18n.nav.home}
                to="/"
                icon={SVGHome}
                onClick={closeMenu}
              />
              {(hasMasterData ||
                hasMemberUsers ||
                hasDocuments ||
                hasBilanzOnline ||
                hasAgreements) && (
                <Nav.Group title={i18n.nav.myBusiness}>
                  {hasMasterData && (
                    <Nav.Item
                      text={i18n.masterData.title}
                      to="/stammdaten"
                      icon={SVGDomain}
                      onClick={closeMenu}
                    />
                  )}
                  {hasMemberUsers && (
                    <Nav.Item
                      text={i18n.memberSettings.title}
                      to="/einstellungen"
                      icon={SVGSupervisorAccount}
                      onClick={closeMenu}
                    />
                  )}
                  {hasDocuments && (
                    <Nav.Item
                      text={i18n.documents.title}
                      to="/dokumente"
                      icon={SVGDescription}
                      onClick={closeMenu}
                    />
                  )}
                  {hasBilanzOnline && (
                    <Nav.Item
                      text={i18n.bilanzOnline.title}
                      to="/kennzahlen"
                      icon={SVGEqualizer}
                      onClick={closeMenu}
                    />
                  )}
                  {hasAgreements && (
                    <Nav.Item
                      text={i18n.agreements.title}
                      to="/vertraege-und-preise"
                      icon={SVGHistoryEdu}
                      onClick={closeMenu}
                    />
                  )}
                </Nav.Group>
              )}
              <Nav.Group title={i18n.nav.services}>
                <Nav.Item
                  text={i18n.departments.accounting.title}
                  to={`/${departmentSlugs.accounting}`}
                  icon={SVGBulletAccounting}
                  onClick={closeMenu}
                />
                <Nav.Item
                  text={i18n.departments.payroll.title}
                  to={`/${departmentSlugs.payroll}`}
                  icon={SVGBulletPayroll}
                  onClick={closeMenu}
                />
                <Nav.Item
                  text={i18n.departments.webMarketing.title}
                  to={`/${departmentSlugs.webMarketing}`}
                  icon={SVGBulletWebMarketing}
                  onClick={closeMenu}
                />
                <Nav.Item
                  text={i18n.departments.joinHospitality.title}
                  to={`/${departmentSlugs.joinHospitality}`}
                  icon={SVGBulletJoinHospitality}
                  onClick={closeMenu}
                />
              </Nav.Group>
            </Nav>
          )}
        </Sidebar>
        {latchedError ? (
          <ErrorPage />
        ) : (
          <Routes>
            <Route index element={<Home />} />
            <Route path="einladungen" element={<Invites />} />
            {hasMasterData && (
              <Route path="stammdaten" element={<MasterData />} />
            )}
            {hasMemberUsers && (
              <Route path="einstellungen/*">
                <Route path="*" element={<MemberSettings />} />
                {hasMemberUsersWrite && (
                  <>
                    <Route
                      path="benutzer/:userId/:companyId"
                      element={<MemberUser />}
                    />
                    <Route path="benutzer/:userId" element={<MemberUser />} />
                  </>
                )}
              </Route>
            )}
            {hasDocuments && (
              <Route path="dokumente/*" element={<Documents />} />
            )}
            {hasBilanzOnline && (
              <Route path="kennzahlen/*" element={<BilanzOnline />} />
            )}
            {hasAgreements && (
              <Route path="vertraege-und-preise/*">
                <Route index element={<Agreements />} />
                <Route
                  path=":id/:version/:showVexatoryClauses/*"
                  element={<AgreementDetail />}
                />
                <Route path=":id/:version/*" element={<AgreementDetail />} />
              </Route>
            )}
            <Route path={departmentSlugs.accounting}>
              <Route index element={<Accounting />} />
              {hasOpenItems && (
                <Route path="offene-posten" element={<OpenItems />} />
              )}
            </Route>
            <Route path={departmentSlugs.payroll}>
              <Route index element={<Payroll />} />
              {hasUniUrg && <Route path="uniurg" element={<UniUrg />} />}
              {hasArbeitssicherheit && (
                <Route
                  path="arbeitssicherheit"
                  element={<Arbeitssicherheit />}
                />
              )}
              {isPayrollCustomer && (
                <Route path="notfalldienst" element={<EmergencyService />} />
              )}
              {isPayrollCustomer && (
                <Route path="arbeit-auf-abruf" element={<ArbeitAufAbruf />} />
              )}
            </Route>
            <Route path={departmentSlugs.webMarketing}>
              <Route index element={<WebMarketing />} />
              {hasDac7 && <Route path="dac7" element={<Dac7 />} />}
            </Route>
            <Route path={departmentSlugs.joinHospitality}>
              <Route index element={<JoinHospitality />} />
              <Route path="join-job-offers" element={<JoinJobOffers />} />
            </Route>
          </Routes>
        )}
      </div>
      <ModalDialog
        title={i18n.joinFutureHospitality.title}
        isOpen={isModalOpen}
        onClose={() => {
          if (localStorage.getItem(POPUP_KEY) === null) {
            setJoinPopupCooldown(2);
          }
          setIsModalOpen(false);
        }}
        actions={[
          {
            title: i18n.joinFutureHospitality.notInterested,
            type: "secondary",
            onClick: () => {
              setJoinPopupCooldown("never");
              setIsModalOpen(false);
            },
          },
          {
            title: i18n.joinFutureHospitality.readinessCheck,
            type: "primary",
            onClick: () => {
              window.open("https://easy-feedback.de/umfrage/1985304/N31LL8");
              setJoinPopupCooldown(6);
              setIsModalOpen(false);
            },
          },
        ]}
      >
        <p>
          {i18n.joinFutureHospitality.paragraph1}
          <br />
          <br />
          {i18n.joinFutureHospitality.paragraph2}
        </p>
      </ModalDialog>
    </>
  );
};

export default CustomerApp;
